<template>
  <div>
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="1024"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar :color="$t(`Order.OrderWebhookHistoryDialog.Toolbar.Color`)">
            <v-icon dark>
              {{ $t(`Order.OrderWebhookHistoryDialog.Toolbar.Icon`) }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ $t(`Order.OrderWebhookHistoryDialog.Toolbar.Title`) }}
            </div>

            <div class="text-caption grey--text">
              {{ $t(`Order.OrderWebhookHistoryDialog.Toolbar.Subtitle`) }} #{{ data?.Order.Id }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="closeDialog"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text
          ref="body"
          :style="isDarkMode ? 'background-color: #323232;' : ''"
          class="px-3 py-0"
        >
          <v-timeline
            align-top
            dense
          >
            <template v-for="(group, key, groupIdx) in itemHistory">
              <v-timeline-item
                :key="`timeline-group-${groupIdx}`"
                class="text-body-1 font-weight-bold pl-2 pb-2"
                hide-dot
              >
                {{ key }}
              </v-timeline-item>

              <template v-for="(item, index) in group">
                <v-timeline-item
                  v-if="item.Attempt === 1 && index > 0"
                  :key="`timeline-group-${groupIdx}-item-${index}-divider`"
                  class="text-body-1 font-weight-bold pl-2 pb-2"
                  hide-dot
                >
                  <v-divider />
                </v-timeline-item>

                <v-timeline-item
                  :key="`timeline-group-${groupIdx}-item-${index}`"
                  :color="getHistoryItemStatus(item).Color"
                  :icon="getHistoryItemStatus(item).Icon"
                  class="pb-4"
                  fill-dot
                >
                  <v-container
                    class="py-2 px-1"
                    fluid
                  >
                    <v-row
                      :dense="$vuetify.breakpoint.xsOnly"
                      :no-gutters="$vuetify.breakpoint.smAndUp"
                    >
                      <v-col :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'">
                        <div style="min-width: 67px">
                          <status-chip
                            v-if="item.User"
                            :avatar-color="item.UserColor ? item.UserColor : 'purple'"
                            :chip-style="{width: $vuetify.breakpoint.xsOnly ? '100%' : 'inherit'}"
                            :class="{'d-flex': $vuetify.breakpoint.xsOnly}"
                            :color="item.UserColor ? item.UserColor : 'purple'"
                            :label="item.UserShortName"
                            :tooltip="item.User"
                            dense
                            icon="mdi-account"
                            label-class="text-left text-sm-center"
                          />
                        </div>
                      </v-col>

                      <v-col :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'">
                        <status-chip
                          :chip-style="{width: $vuetify.breakpoint.xsOnly ? '100%' : 'inherit'}"
                          :class="{'d-flex': $vuetify.breakpoint.xsOnly}"
                          :label="item.Time"
                          :min-width="90"
                          avatar-color="grey darken-1"
                          color="grey darken-1"
                          dense
                          icon="mdi-clock"
                          label-class="text-left text-sm-center"
                        />
                      </v-col>

                      <v-col :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'">
                        <status-chip
                          :avatar-color="item.IsManual ? 'indigo' : 'teal'"
                          :chip-style="{width: $vuetify.breakpoint.xsOnly ? '100%' : 'inherit'}"
                          :class="{'d-flex': $vuetify.breakpoint.xsOnly}"
                          :color="item.IsManual ? 'indigo' : 'teal'"
                          :icon="item.IsManual ? 'mdi-account-convert' : 'mdi-refresh-auto'"
                          :label="`Attempt #${item.Attempt}`"
                          :tooltip="humanizedDuration(item.Sleep)"
                          dense
                          label-class="text-left text-sm-center"
                        />
                      </v-col>

                      <v-col :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'">
                        <status-chip
                          v-if="getHistoryItemStatus(item)"
                          :avatar-color="`${getHistoryItemStatus(item).Color} darken-2`"
                          :chip-style="{width: $vuetify.breakpoint.xsOnly ? '100%' : 'inherit'}"
                          :class="{'d-flex': $vuetify.breakpoint.xsOnly}"
                          :color="getHistoryItemStatus(item).Color"
                          :icon="getHistoryItemStatus(item).Icon"
                          :label="getHistoryItemStatus(item).Name"
                          :min-width="115"
                          :tooltip="getHistoryItemStatus(item).Tooltip"
                          dense
                          label-class="text-left text-sm-center"
                        />
                      </v-col>

                      <v-col :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'">
                        <order-status-chip
                          v-if="item.StatusId"
                          :order="item"
                          dense
                        />
                      </v-col>

                      <v-col
                        v-if="userIsSuperAdmin"
                        :cols="$vuetify.breakpoint.xsOnly ? '12' : 'auto'"
                        class="grow text-end"
                      >
                        <v-btn
                          v-if="item.Attempt === 1"
                          :disabled="item.Attempt !== 1"
                          :href="getHistoryItemRequestDataUrl(item)"
                          class="px-2 ml-1 mr-1"
                          color="info"
                          dark
                          depressed
                          height="24"
                          small
                          style="min-width: 0;"
                          target="_blank"
                        >
                          <v-icon left>
                            mdi-code-block-braces
                          </v-icon>

                          REQ
                        </v-btn>

                        <v-btn
                          :href="getHistoryItemResponseDataUrl(item)"
                          class="px-2"
                          color="brown"
                          dark
                          depressed
                          height="24"
                          small
                          style="min-width: 0;"
                          target="_blank"
                        >
                          <v-icon left>
                            mdi-code-block-braces
                          </v-icon>

                          RES
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-timeline-item>
              </template>

              <v-timeline-item
                :key="`timeline-group-${groupIdx}-divider`"
                class="text-body-1 font-weight-bold pl-2 pb-2"
                hide-dot
              >
                <v-divider />
              </v-timeline-item>
            </template>
          </v-timeline>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <template v-if="posUserCan('ORDERS', 'MANAGE')">
            <v-menu
              close-on-click
              close-on-content-click
              offset-y
            >
              <template #activator="{on, attrs, value}">
                <v-btn
                  color="indigo"
                  dark
                  depressed
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ $t('Common.Button.Retry').toLocaleUpperCase($i18n.locale) }}

                  <v-divider
                    class="ml-2"
                    vertical
                  />

                  <v-icon right>
                    {{ value ? 'mdi-menu-up' : 'mdi-menu-down' }}
                  </v-icon>
                </v-btn>
              </template>

              <v-list
                class="pa-0"
                dense
              >
                <v-list-item
                  class="orange"
                  @click="$emit('retry', {Order: data.Order, RetryOnce: true})"
                >
                  <v-list-item-avatar size="24">
                    <v-icon>mdi-repeat-once</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-title>{{ $t('Common.Button.RetryOnce').toLocaleUpperCase($i18n.locale) }}</v-list-item-title>
                </v-list-item>

                <v-list-item
                  class="info"
                  @click="$emit('retry', {Order: data.Order, RetryOnce: false})"
                >
                  <v-list-item-avatar size="24">
                    <v-icon>mdi-repeat</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-title>{{ $t('Common.Button.RetryAll').toLocaleUpperCase($i18n.locale) }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <v-spacer />

          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            @click="closeDialog"
          >
            {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import DialogVisible from '@/mixins/dialogVisible'
import StatusChip from '@/components/common/StatusChip.vue'
import OrderStatusChip from '@/components/orders/common/OrderStatusChip.vue'

export default {
  name      : 'OrderWebhookHistoryDialog',
  components: {OrderStatusChip, StatusChip},
  mixins    : [Auth, AppData, DialogVisible],
  props     : {
    data: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {}
  },
  computed: {
    itemHistory() {
      const history = this.data?.History || []

      history.forEach((item) => {
        [item.Date, item.Time] = (item?.CreatedAt || '').split(' ')
      })

      const historyGroupedByDate = Object.groupBy(history, item => item.Date)

      return historyGroupedByDate
    }
  },
  watch: {
    isVisible(val) {
      if (this.$refs.body) this.$refs.body.scrollTop = 0
    }
  },
  methods: {
    humanizedDuration(durationInSeconds) {
      const dateObj = new Date(durationInSeconds * 1000)
      const hours = dateObj.getUTCHours()
      const minutes = dateObj.getUTCMinutes()
      const seconds = dateObj.getSeconds()
      return hours.toString().padStart(2, '0') + ':' +
          minutes.toString().padStart(2, '0') + ':' +
          seconds.toString().padStart(2, '0')
    },

    getHistoryItemStatus(historyItem) {
      const webhook = historyItem
      const successful = webhook?.StatusCode >= 200 && webhook?.StatusCode <= 299
      const langObj = webhook?.StatusCode ? successful ? this.$t('Order.OrdersAll.ListView.WebhookStatus.Success') : this.$t('Order.OrdersAll.ListView.WebhookStatus.Fail') : this.$t('Order.OrdersAll.ListView.WebhookStatus.Unknown')
      const tooltip = webhook?.StatusCode ? successful ? `${webhook.CreatedAt} <br> ${langObj.Name} (${webhook.StatusCode})` : `${webhook.CreatedAt} <br> ${langObj.Name} (${webhook.StatusCode}) <br> ${webhook.Reason}` : ''

      return {
        Color  : langObj.Color,
        Icon   : langObj.Icon,
        Name   : langObj.Name,
        Tooltip: tooltip
      }
    },
    getHistoryItemRequestDataUrl(item) {
      if (!item || !import.meta.env.VITE_API_URL || !this.authorizationToken) return ''

      const API_URL = import.meta.env.VITE_API_URL
      const ID = item.OrderWebhookId
      const TOKEN = this.authorizationToken

      return `${API_URL}raw/order/webhook/${ID}/payload/${TOKEN}`
    },

    getHistoryItemResponseDataUrl(item) {
      if (!item || !import.meta.env.VITE_API_URL || !this.authorizationToken) return ''

      const API_URL = import.meta.env.VITE_API_URL
      const ID = item.OrderWebhookId
      const TOKEN = this.authorizationToken

      return `${API_URL}raw/order/webhook/${ID}/response/${TOKEN}`
    }
  }
}

</script>

<style scoped>

</style>
